import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  FileCheck2,
  FileText,
  Info,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  MessageCircleQuestion,
  Phone,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  UserRound,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";
import { DocumentPreview } from "../components/DocumentPreview.jsx";
const categoryIcons = {
  date: CalendarDays,
  amount: CircleDollarSign,
  action: ListChecks,
  person: UserRound,
  condition: FileCheck2,
  contact: Phone,
  other: Info,
};
const urgencyLabels = {
  now: "Ahora",
  soon: "Pronto",
  when_possible: "Cuando puedas",
};

const demoDocument = Object.freeze({
  id: "ejemplo-civita",
  notebookId: "ejemplo",
  sourceId: "ejemplo",
  fileName: "comunicacion-de-servicio.pdf",
  demo: true,
  sourceStatus: "ready",
  analysisStatus: "ready",
  status: "ready",
  pageCount: 1,
  question: "¿Qué cambia y tengo que hacer algo?",
  createdAt: new Date().toISOString(),
  analysis: {
    title: "Actualización del precio de un servicio",
    documentType: "Comunicación de una empresa",
    summary:
      "La empresa informa que cambiará el precio mensual del servicio. El nuevo valor sería de $48.500 desde el 1 de octubre de 2026 y menciona la posibilidad de pedir la baja si no estás de acuerdo.",
    highlights: [
      {
        category: "date",
        tone: "info",
        label: "Fecha del cambio",
        text: "El nuevo valor se aplicaría desde el 1 de octubre de 2026.",
        page: 1,
        quote: "a partir del 1 de octubre de 2026",
      },
      {
        category: "amount",
        tone: "info",
        label: "Nuevo valor mensual",
        text: "$48.500 por mes.",
        page: 1,
        quote: "se actualizará el valor mensual del servicio a $48.500",
      },
      {
        category: "condition",
        tone: "info",
        label: "No exige una respuesta",
        text:
          "El texto comunica un cambio y ofrece una alternativa. No dice que tengas que contestar para conservar el servicio.",
        page: 1,
        quote: "Si no está de acuerdo, podrá solicitar la baja",
      },
      {
        category: "condition",
        tone: "caution",
        label: "Falta identificar el servicio",
        text: "Compará la referencia con tu factura para confirmar que la comunicación corresponda a tu cuenta.",
        page: null,
        quote: null,
      },
    ],
    nextActions: [
      {
        text: "Guardá esta comunicación junto con tu próxima factura.",
        urgency: "soon",
      },
      {
        text: "Verificá el canal de atención en el sitio oficial antes de llamar.",
        urgency: "when_possible",
      },
    ],
    initialQuestion: {
      question: "¿Qué cambia y tengo que hacer algo?",
      answer:
        "Cambia el precio mensual desde la fecha indicada. El documento no dice que debas responder obligatoriamente; sí ofrece pedir la baja si no estás de acuerdo.",
      pageReferences: [1],
    },
    needsProfessionalHelp: false,
    professionalHelpReason: null,
  },
});

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function StatusStep({ state, children }) {
  return (
    <li className={`processing-step is-${state}`}>
      <span>
        {state === "done"
          ? <Check size={15} />
          : state === "active"
            ? <LoaderCircle className="is-spinning" size={15} />
            : null}
      </span>
      {children}
    </li>
  );
}

function ProcessingView({ document, onCancel }) {
  const extracted = document.sourceStatus === "ready";
  const analyzing = extracted && document.analysisStatus !== "ready";
  return (
    <main className="civita-document-page processing-view">
      <button className="text-button" type="button" onClick={onCancel}>
        <ArrowLeft size={16} /> Volver
      </button>
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon">
          <Sparkles size={25} />
        </span>
        <span className="eyebrow">Entender un documento</span>
        <h1>Estamos preparando una explicación clara</h1>
        <p>{document.fileName}</p>
        <ol>
          <StatusStep state="done">Archivo recibido de forma segura</StatusStep>
          <StatusStep state={extracted ? "done" : "active"}>
            Leyendo el texto y reconociendo sus páginas
          </StatusStep>
          <StatusStep state={analyzing ? "active" : "pending"}>
            Separando datos importantes, consecuencias y dudas
          </StatusStep>
        </ol>
        <small>Podés dejar esta pantalla abierta. Esto suele demorar menos de un minuto.</small>
      </section>
    </main>
  );
}

function ErrorView({ document, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>Necesitamos intentar otra vez</h1>
        <p>{document.error?.message || "Ocurrió un problema al leer o explicar el documento."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />}
            Reintentar
          </button>
          <button className="secondary-button" type="button" onClick={onBack}>Elegir otro PDF</button>
        </div>
      </section>
    </main>
  );
}

function CiteButton({ page, quote, onSelect }) {
  if (!page || !quote) return null;
  return (
    <button
      className="citation-button"
      type="button"
      onClick={() => onSelect(page)}
      title={quote}
    >
      Ver página {page} <ChevronRight size={13} />
    </button>
  );
}

function AnalysisResult({
  api,
  document,
  onBack,
  onDelete,
}) {
  const [page, setPage] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("page"));
    return Number.isInteger(requested) && requested > 0 ? requested : 1;
  });
  const previewRef = useRef(null);
  const analysis = document.analysis;
  const conversationMetadata = useMemo(() => ({
    civitaDocumentId: document.id,
    notebookId: document.notebookId,
  }), [document.id, document.notebookId]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [document.id]);

  function selectCitation(nextPage) {
    setPage(nextPage);
    previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <main className="civita-document-page result-page">
      {document.demo && (
        <div className="demo-banner">
          <Sparkles size={16} />
          Este es un ejemplo ilustrativo. Ningún archivo fue enviado ni analizado.
        </div>
      )}
      <header className="result-header">
        <button className="text-button" type="button" onClick={onBack}>
          <ArrowLeft size={16} /> Mis documentos
        </button>
        <div className="result-header__actions">
          <span><FileText size={15} /> {document.fileName}</span>
          {!document.demo && (
            <button className="danger-icon-button" type="button" onClick={onDelete} title="Eliminar documento">
              <Trash2 size={17} />
            </button>
          )}
        </div>
      </header>

      <section className="result-title">
        <div>
          <span className="eyebrow">{analysis.documentType}</span>
          <h1>{analysis.title}</h1>
        </div>
        <span className="analysis-ready"><CheckCircle2 size={16} /> Explicación lista</span>
      </section>

      <div className="analysis-workspace">
        <div className="analysis-content">
          <section className="plain-summary">
            <span className="section-icon">
              {analysis.initialQuestion ? <MessageCircleQuestion size={18} /> : <Sparkles size={18} />}
            </span>
            <div>
              <span className="eyebrow">
                {analysis.initialQuestion ? "Respuesta a tu pregunta" : "En pocas palabras"}
              </span>
              {analysis.initialQuestion && <h2>{analysis.initialQuestion.question}</h2>}
              <p>{analysis.initialQuestion?.answer || analysis.summary}</p>
              {analysis.initialQuestion?.pageReferences.length > 0 && (
                <button
                  className="citation-button"
                  type="button"
                  onClick={() => selectCitation(analysis.initialQuestion.pageReferences[0])}
                >
                  Ver en el documento <ChevronRight size={13} />
                </button>
              )}
            </div>
          </section>

          {analysis.highlights.length > 0 && (
            <section className="analysis-section">
              <header>
                <span className="section-number">1</span>
                <div>
                  <h2>Lo esencial</h2>
                  <p>Hasta cuatro puntos para entender el documento.</p>
                </div>
              </header>
              <div className="document-highlights">
                {analysis.highlights.map((item, index) => {
                  const Icon = categoryIcons[item.category] || Info;
                  return (
                    <article className={`document-highlight is-${item.tone}`} key={`${item.label}-${index}`}>
                      <span className="key-point__icon"><Icon size={18} /></span>
                      <div>
                        <small>{item.label}</small>
                        <strong>{item.text}</strong>
                        <CiteButton page={item.page} quote={item.quote} onSelect={selectCitation} />
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {analysis.nextActions.length > 0 && (
            <section className="analysis-section">
              <header>
                <span className="section-number">2</span>
                <div>
                  <h2>Qué podés hacer ahora</h2>
                  <p>Sólo los próximos pasos útiles.</p>
                </div>
              </header>
              <ol className="next-steps">
                {analysis.nextActions.map((item, index) => (
                  <li key={`${item.text}-${index}`}>
                    <span>{index + 1}</span>
                    <div>
                      <small>{urgencyLabels[item.urgency]}</small>
                      <strong>{item.text}</strong>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {analysis.needsProfessionalHelp && (
            <section className="professional-note">
              <ShieldCheck size={21} />
              <div>
                <h2>Puede ser útil una consulta profesional</h2>
                <p>{analysis.professionalHelpReason}</p>
              </div>
            </section>
          )}

          <p className="ai-disclaimer">
            Esta explicación fue generada con IA y puede equivocarse. Compará siempre los puntos importantes con el documento original.
          </p>
        </div>

        <div className="preview-column" ref={previewRef}>
          <DocumentPreview
            api={api}
            document={document}
            page={page}
            onPageChange={setPage}
          />
        </div>
      </div>

      <CivitaAssistantLauncher
        title="Seguí preguntando sobre este documento"
        description="Justiniano se abrirá sobre esta pantalla y responderá usando solamente este PDF."
        sourceLabel="Solo este documento"
        conversationScope={`documento:${document.id}:v2`}
        conversationMetadata={conversationMetadata}
        notebookIds={[document.notebookId]}
        requestedSources={["library"]}
        welcome="Ya procesamos el documento completo. Preguntame qué dice, qué fechas o montos aparecen, o qué puede ser importante. Si alguna parte no fue legible, te lo voy a decir."
        suggestions={[
          "¿Hay algo que tenga que hacer ahora?",
          "¿Qué fechas y montos aparecen?",
          "¿Qué información falta para estar seguro?",
        ]}
        appContext={{
          route: "/entender-documento",
          activeEntity: {
            type: "civita_document",
            id: document.id,
            label: document.fileName,
          },
          document: {
            fileName: document.fileName,
            initialQuestion: document.question,
            pageCount: document.pageCount,
            warnings: document.warnings,
            analysis,
          },
        }}
      />
    </main>
  );
}

export function UnderstandDocumentPage({ api, authEnabled }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [question, setQuestion] = useState("");
  const [dragging, setDragging] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(authEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectDocument(document) {
    setSelected(document);
    if (!document?.demo) {
      window.history.replaceState(
        {},
        "",
        `/entender-documento?document=${encodeURIComponent(document.id)}`,
      );
    }
  }

  function resetView() {
    setSelected(null);
    setFiles([]);
    setQuestion("");
    window.history.replaceState({}, "", "/entender-documento");
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listDocuments()
      .then((items) => {
        if (!active) return;
        setDocuments(items);
        const parameters = new URLSearchParams(window.location.search);
        const requestedId = parameters.get("document");
        const requestedNotebook = parameters.get("notebook");
        const requestedSource = parameters.get("source");
        const match = items.find((item) => (
          (requestedId && item.id === requestedId)
          || (requestedNotebook && item.notebookId === requestedNotebook)
          || (requestedSource && item.sourceId === requestedSource)
        ));
        if (match) setSelected(match);
      })
      .catch((error) => showToast({ message: error.message, tone: "danger" }))
      .finally(() => active && setLoadingHistory(false));
    return () => {
      active = false;
    };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || selected.status === "ready" || selected.status === "failed") {
      return undefined;
    }
    let active = true;
    let timer;
    async function refresh() {
      try {
        let current = await api.getDocument(selected.id);
        if (current.sourceStatus === "ready" && current.analysisStatus === "waiting") {
          current = await api.analyzeDocument(current.id);
        }
        if (!active) return;
        setSelected(current);
        setDocuments((items) => items.map((item) =>
          item.id === current.id ? current : item));
        if (current.status !== "ready" && current.status !== "failed") {
          timer = window.setTimeout(refresh, 1_800);
        }
      } catch (error) {
        if (!active) return;
        if (error.code === "civita_document_not_ready") {
          timer = window.setTimeout(refresh, 1_800);
          return;
        }
        setSelected((current) => current
          ? { ...current, status: "failed", error: { message: error.message, code: error.code } }
          : current);
      }
    }
    void refresh();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  function chooseFiles(fileList) {
    const nextFiles = [...(fileList || [])];
    if (!nextFiles.length) return;
    if (nextFiles.length > 10) {
      showToast({ message: "Podés subir hasta 10 fotos.", tone: "danger" });
      return;
    }
    const invalid = nextFiles.some((nextFile) => ![
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(nextFile.type) && !/\.(?:pdf|jpe?g|png|webp)$/i.test(nextFile.name));
    if (invalid) {
      showToast({ message: "Usá un PDF o fotos en formato JPG, PNG o WEBP.", tone: "danger" });
      return;
    }
    const hasPdf = nextFiles.some((nextFile) => (
      nextFile.type === "application/pdf" || /\.pdf$/i.test(nextFile.name)
    ));
    if (hasPdf && nextFiles.length > 1) {
      showToast({ message: "Subí un solo PDF o varias fotos, sin mezclar formatos.", tone: "danger" });
      return;
    }
    const totalSize = nextFiles.reduce((total, nextFile) => total + nextFile.size, 0);
    if (totalSize > 20 * 1024 * 1024) {
      showToast({ message: "Los archivos superan el límite total de 20 MB.", tone: "danger" });
      return;
    }
    setFiles(nextFiles);
  }

  async function submit(event) {
    event.preventDefault();
    if (!files.length || submitting) return;
    if (!authEnabled) {
      selectDocument(demoDocument);
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.createDocument(files, question);
      setDocuments((items) => [created, ...items]);
      selectDocument(created);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setSubmitting(false);
    }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retryDocument(selected.id);
      setSelected(current);
      setDocuments((items) => items.map((item) =>
        item.id === current.id ? current : item));
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setRetrying(false);
    }
  }

  async function remove() {
    if (!selected || deleting) return;
    setDeleting(true);
    try {
      await api.deleteDocument(selected.id);
      setDocuments((items) => items.filter((item) => item.id !== selected.id));
      setDeleteOpen(false);
      resetView();
      showToast({ message: "El documento fue eliminado.", tone: "success" });
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setDeleting(false);
    }
  }

  if (selected?.status === "ready" && selected.analysis) {
    return (
      <>
        <AnalysisResult
          api={api}
          document={selected}
          onBack={resetView}
          onDelete={() => setDeleteOpen(true)}
        />
        {deleteOpen && (
          <ConfirmDialog
            title="¿Eliminar este documento?"
            description="Se borrarán el PDF, su explicación y las fuentes asociadas. Esta acción no se puede deshacer."
            confirmLabel="Eliminar"
            busy={deleting}
            onClose={() => !deleting && setDeleteOpen(false)}
            onConfirm={remove}
          />
        )}
      </>
    );
  }
  if (selected?.status === "failed") {
    return (
      <ErrorView
        document={selected}
        retrying={retrying}
        onRetry={retry}
        onBack={resetView}
      />
    );
  }
  if (selected) return <ProcessingView document={selected} onCancel={resetView} />;

  return (
    <main className="civita-document-page upload-page">
      <header className="upload-heading">
        <span className="eyebrow">Entender un documento</span>
        <h1>Subí el documento.<br />Te explicamos qué dice.</h1>
        <p>
          Encontramos las partes importantes, las explicamos en lenguaje claro y te mostramos la página original para que puedas verificarlas.
        </p>
      </header>

      <div className="upload-layout">
        <form className="upload-form" onSubmit={submit}>
          <div
            className={`drop-zone ${dragging ? "is-dragging" : ""} ${files.length ? "has-file" : ""}`}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              chooseFiles(event.dataTransfer.files);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp"
              multiple
              onChange={(event) => chooseFiles(event.target.files)}
            />
            {files.length ? (
              <div className="selected-file">
                <span><FileCheck2 size={24} /></span>
                <div>
                  <strong>{files.length === 1 ? files[0].name : `${files.length} fotos seleccionadas`}</strong>
                  <small>{formatSize(files.reduce((total, item) => total + item.size, 0))} · listo para analizar</small>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setFiles([]);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  aria-label="Quitar archivo"
                >
                  <X size={17} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileInputRef.current?.click()}>
                <span className="drop-zone__icon"><UploadCloud size={28} /></span>
                <strong>Arrastrá tu PDF o tus fotos acá</strong>
                <small>PDF, JPG, PNG o WEBP · hasta 10 fotos y 20 MB</small>
              </button>
            )}
          </div>

          <label className="question-field">
            <span>
              ¿Qué querés entender? <small>Opcional</small>
            </span>
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value.slice(0, 1_000))}
              placeholder="Por ejemplo: ¿tengo que responder?, ¿hay alguna fecha importante?"
              rows={3}
            />
          </label>

          <button className="analyze-button" type="submit" disabled={!files.length || submitting}>
            {submitting ? <LoaderCircle className="is-spinning" size={18} /> : <Sparkles size={18} />}
            {submitting ? "Subiendo documento…" : "Explicarme este documento"}
            {!submitting && <ArrowRight size={18} />}
          </button>

          {!authEnabled && (
            <button className="example-button" type="button" onClick={() => selectDocument(demoDocument)}>
              Ver un ejemplo completo
            </button>
          )}

          <p className="privacy-note">
            <LockKeyhole size={14} />
            Tu documento queda protegido en tu espacio y no se comparte con otros usuarios.
          </p>
        </form>

        <aside className="how-it-works">
          <span className="eyebrow">Qué vas a recibir</span>
          <ol>
            <li>
              <span>1</span>
              <div><strong>Un resumen claro</strong><small>Qué es y para qué sirve el documento.</small></div>
            </li>
            <li>
              <span>2</span>
              <div><strong>Los datos importantes</strong><small>Fechas, montos, acciones y condiciones.</small></div>
            </li>
            <li>
              <span>3</span>
              <div><strong>Lo que falta saber</strong><small>Sin inventar ni completar datos dudosos.</small></div>
            </li>
          </ol>
          <div className="scope-note">
            <ShieldCheck size={18} />
            <p><strong>Siempre podés verificarlo.</strong> Cada punto importante vuelve a la página que lo respalda.</p>
          </div>
        </aside>
      </div>

      {(loadingHistory || documents.length > 0) && (
        <section className="recent-documents">
          <header>
            <div>
              <span className="eyebrow">Tus documentos</span>
              <h2>Análisis recientes</h2>
            </div>
          </header>
          {loadingHistory ? (
            <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Cargando…</div>
          ) : (
            <div className="document-list">
              {documents.map((document) => (
                <button type="button" key={document.id} onClick={() => selectDocument(document)}>
                  <span className="document-list__icon"><FileText size={19} /></span>
                  <span>
                    <strong>{document.analysis?.title || document.fileName}</strong>
                    <small>{formatDate(document.createdAt)} · {document.pageCount ? `${document.pageCount} pág.` : "Procesando"}</small>
                  </span>
                  {document.status === "ready"
                    ? <CheckCircle2 className="is-ready" size={18} />
                    : document.status === "failed"
                      ? <AlertCircle className="is-failed" size={18} />
                      : <Clock3 className="is-pending" size={18} />}
                  <ChevronRight size={17} />
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
